import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useThemeStore } from "./store/theme";
import { useCookieConsentStore } from "./store/cookieConsent";
import ErrorBoundary from "./components/ErrorBoundary";
import { CookieConsent } from "./components/CookieConsent";
import { initGA4, trackPageView } from "./lib/ga4";

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// Lazy load public page components
const LandingPage = lazy(() => import("./pages/LandingPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const RefundPage = lazy(() => import("./pages/RefundPage"));
const ApplyPage = lazy(() => import("./pages/ApplyPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const EmailVerificationPage = lazy(() =>
  import("./pages/EmailVerificationPage").then((m) => ({
    default: m.EmailVerificationPage,
  })),
);
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

/** Track SPA route changes for GA4 */
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
}

function App() {
  const { initializeTheme } = useThemeStore();
  const consentStatus = useCookieConsentStore((s) => s.status);

  useEffect(() => {
    initializeTheme();
  }, [initializeTheme]);

  // Initialize GA4 only after user accepts cookies
  useEffect(() => {
    if (consentStatus === "accepted") {
      initGA4();
    }
  }, [consentStatus]);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <RouteTracker />
        <Toaster richColors position="top-right" />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes for loyrush.com */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/refund" element={<RefundPage />} />
            <Route path="/apply" element={<ApplyPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/verify-email" element={<EmailVerificationPage />} />

            {/* 404 — honest "not found" page (noindex) instead of duplicating the landing
                under every unknown URL. Bots treating SPA fallback as duplicate content
                was actively hurting crawl quality. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
        <CookieConsent />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
